import api from "@/api";
import converters from "@/converters";
import type { CategoryFragment } from "@/graphql";
import { Category_Type, type CategoryInput } from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import type { ICachedData, ICategoryAPI } from "@/interfaces";
import type { CategoryViews } from "@/types";
import { computed, ref } from "vue";

const DEFAULT_DATA: ICachedData<CategoryFragment[]> = {
  status: "none",
  data: [],
};

const API_MAP: Record<CategoryViews, ICategoryAPI> = {
  activities: api.categories,
  news: api.categories,
  meetings: api.categories,
};

const CATEGORY_TYPE_MAP: Record<CategoryViews, Category_Type> = {
  activities: Category_Type.Activity,
  news: Category_Type.News,
  meetings: Category_Type.Meeting,
};

const dataMap = ref<Record<CategoryViews, ICachedData<CategoryFragment[]>>>({
  activities: { ...DEFAULT_DATA },
  news: { ...DEFAULT_DATA },
  meetings: { ...DEFAULT_DATA },
});

export function useCategories(view: CategoryViews, isInit: boolean = false) {
  const currentAPI = API_MAP[view];
  const type = CATEGORY_TYPE_MAP[view];

  const init = async () => {
    const item = dataMap.value[view];

    if (item.status === "loaded" || isInit) {
      return;
    }

    item.status = "loading";
    const data = await currentAPI.getCategories(type);
    item.data = data || [];
    item.status = "loaded";
  };

  init();

  const categories = computed(() => dataMap.value[view].data);

  const categoriesList = computed(() => {
    return dataMap.value[view].data
      .map(converters.categories.toMultiSelectItem)
      .toSorted((a, b) => a.title.localeCompare(b.title));
  });

  const categoriesOptions = computed(() => {
    return dataMap.value[view].data
      .map(converters.categories.toOption)
      .toSorted((a, b) => a.title.localeCompare(b.title));
  });

  const loading = computed(() => {
    return dataMap.value[view].status === "loading";
  });

  const status = computed(() => dataMap.value[view].status);

  const isReady = () => isDataReady(status);

  const refetch = async () => {
    const data = await currentAPI.getCategories(type);
    dataMap.value[view].data = data || [];
  };

  const save = async (input: CategoryInput) => {
    const response = await currentAPI.save(type, input);
    await refetch();
    return response;
  };

  const deleteItem = async (id: number) => {
    const response = await currentAPI.deleteItem(type, id.toString());
    await refetch();
    return response;
  };

  const getEntityCountOnRelatedCategory = async (id: number) => {
    return await currentAPI.getEntityCountOnRelatedCategory(type, id);
  };

  return {
    categories,
    categoriesList,
    categoriesOptions,
    loading,
    refetch,
    isReady,
    save,
    deleteItem,
    getEntityCountOnRelatedCategory,
  };
}
